import React, { ChangeEvent, FC, ReactNode, useCallback } from 'react';
import { useController } from 'react-hook-form';
import classNames from 'classnames';

import styles from './fieldStyles.scss';

const CheckboxField: FC<{
  disabled?: boolean;
  generateHelperElement?: (checked: boolean) => ReactNode;
  label: string;
  name: string;
  onChangeCallback?: (checked: boolean) => void;
  size?: 'small' | 'medium' | 'large';
}> = ({ disabled, generateHelperElement, label, name, onChangeCallback, size = 'medium' }) => {
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name });

  const isDisabled = disabled || isSubmitting;

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      onChange(checked);
      if (onChangeCallback) onChangeCallback(checked);
    },
    [onChange, onChangeCallback]
  );

  return (
    <div
      className={classNames(styles.checkboxField, {
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large'
      })}
    >
      <input
        {...field}
        checked={Boolean(value)}
        disabled={isDisabled}
        id={name}
        onChange={handleOnChange}
        type="checkbox"
        value={value || ''}
      />
      <div className={styles.inputWrapper}>
        <label data-title htmlFor={name}>
          {label}
        </label>
        {error ? (
          <label data-error htmlFor={name}>
            {error.message}
          </label>
        ) : null}
        {generateHelperElement ? generateHelperElement(Boolean(value)) : null}
      </div>
    </div>
  );
};

export default CheckboxField;
